import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, BASE, getAuthHeaders } from '@/api/client'

// ─── Types ───

export interface YouTubeChannel {
  id: string
  channelId: string
  channelTitle: string
  channelThumbnail: string | null
  subscriberCount: number
  videoCount: number
  viewCount: number
  tags: string[]
  userId: string
  userName: string | null
  createdAt: number
}

export interface YouTubeVideo {
  id: string
  videoId: string
  title: string
  description: string | null
  thumbnailUrl: string | null
  privacyStatus: 'public' | 'unlisted' | 'private'
  tags: string[]
  viewCount: number
  likeCount: number
  commentCount: number
  duration: string | null
  publishedAt: number | null
  syncedAt: number | null
}

export interface YouTubeComment {
  id: string
  commentId: string
  parentId: string | null
  authorName: string
  authorChannelId: string | null
  authorProfileImage: string | null
  text: string
  likeCount: number
  publishedAt: number | null
  isReplied: boolean
}

export interface YouTubeAnalyticsTotals {
  views: number
  estimatedMinutesWatched: number
  averageViewDuration: number
  likes: number
  comments: number
  shares: number
  subscribersGained: number
  subscribersLost: number
  revenue: number | null
}

export interface YouTubeAnalyticsDailyEntry {
  day: string
  views: number
  estimatedMinutesWatched: number
}

export interface YouTubeAnalyticsData {
  totals: YouTubeAnalyticsTotals
  dailyData: YouTubeAnalyticsDailyEntry[]
  period: string
  startDate: string
  endDate: string
}

// ─── Channels ───

export function useYouTubeChannels() {
  return useQuery<YouTubeChannel[]>({
    queryKey: ['youtube', 'channels'],
    queryFn: () => api.get('/youtube/channels'),
  })
}

export function useSyncYouTubeChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<{ ok: boolean }>(`/youtube/channels/${channelId}/sync`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

export function useDisconnectYouTubeChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (channelId: string) =>
      api.delete<{ ok: boolean }>(`/youtube/channels/${channelId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['youtube'] })
    },
  })
}

export function useSaveYouTubeChannelTags() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, tags }: { channelId: string; tags: string[] }) =>
      api.put<{ ok: boolean; tags: string[] }>(`/youtube/channels/${channelId}/tags`, { tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

// ─── Videos ───

export function useYouTubeVideos(channelId: string | null) {
  return useQuery<YouTubeVideo[]>({
    queryKey: ['youtube', 'videos', channelId],
    queryFn: () => api.get(`/youtube/channels/${channelId}/videos`),
    enabled: !!channelId,
  })
}

export function useSyncYouTubeVideos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<{ ok: boolean; synced: number }>(`/youtube/channels/${channelId}/sync-videos`),
    onSuccess: (_, channelId) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'videos', channelId] })
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

export function useUpdateYouTubeVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, videoId, ...data }: {
      channelId: string
      videoId: string
      title?: string
      description?: string
      tags?: string[]
      privacyStatus?: string
    }) => api.put<{ ok: boolean }>(`/youtube/channels/${channelId}/videos/${videoId}`, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'videos', vars.channelId] })
    },
  })
}

export function useUploadYouTubeVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, file, title, description, tags, privacyStatus, onProgress }: {
      channelId: string
      file: File
      title: string
      description?: string
      tags?: string
      privacyStatus?: string
      onProgress?: (percent: number) => void
    }) => {
      return new Promise<{ ok: boolean; videoId: string }>((resolve, reject) => {
        const fd = new FormData()
        fd.append('video', file)
        fd.append('title', title)
        if (description) fd.append('description', description)
        if (tags) fd.append('tags', tags)
        if (privacyStatus) fd.append('privacyStatus', privacyStatus)

        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${BASE}/youtube/channels/${channelId}/upload`)
        xhr.setRequestHeader('Accept', 'application/json')
        const authH = getAuthHeaders()
        if (authH.Authorization) xhr.setRequestHeader('Authorization', authH.Authorization)

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100))
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)) } catch { reject(new Error('Resposta invalida')) }
          } else {
            try {
              const body = JSON.parse(xhr.responseText)
              reject(new Error(body.error || `HTTP ${xhr.status}`))
            } catch { reject(new Error(`HTTP ${xhr.status}`)) }
          }
        }
        xhr.onerror = () => reject(new Error('Erro de rede'))
        xhr.timeout = 2700000
        xhr.send(fd)
      })
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'videos', vars.channelId] })
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

export function useUploadYouTubeVideoFromUrl() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, videoUrl, title, description, tags, privacyStatus }: {
      channelId: string
      videoUrl: string
      title: string
      description?: string
      tags?: string
      privacyStatus?: string
    }) => api.post<{ ok: boolean; videoId: string }>(`/youtube/channels/${channelId}/upload`, {
      videoUrl,
      title,
      description,
      tags,
      privacyStatus,
    }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'videos', vars.channelId] })
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

// ─── Comments ───

export function useYouTubeComments(channelId: string | null, videoId: string | null) {
  return useQuery<YouTubeComment[]>({
    queryKey: ['youtube', 'comments', channelId, videoId],
    queryFn: () => api.get(`/youtube/channels/${channelId}/videos/${videoId}/comments`),
    enabled: !!channelId && !!videoId,
  })
}

export function useSyncYouTubeComments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, videoId }: { channelId: string; videoId: string }) =>
      api.post<{ ok: boolean; synced: number }>(`/youtube/channels/${channelId}/videos/${videoId}/sync-comments`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'comments', vars.channelId, vars.videoId] })
    },
  })
}

export function useReplyToYouTubeComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, videoId, commentId, text }: {
      channelId: string
      videoId: string
      commentId: string
      text: string
    }) => api.post<{ ok: boolean }>(`/youtube/channels/${channelId}/videos/${videoId}/reply`, { commentId, text }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'comments', vars.channelId, vars.videoId] })
    },
  })
}

// ─── Default Description ───

export interface YouTubeDefaultDescription {
  defaultDescription: string
  defaultTags: string
}

export function useYouTubeDefaultDescription(channelId: string | null) {
  return useQuery<YouTubeDefaultDescription>({
    queryKey: ['youtube', 'default-description', channelId],
    queryFn: () => api.get(`/youtube/channels/${channelId}/default-description`),
    enabled: !!channelId,
  })
}

export function useUpdateYouTubeDefaultDescription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, defaultDescription, defaultTags }: {
      channelId: string
      defaultDescription: string
      defaultTags: string
    }) => api.put<{ ok: boolean }>(`/youtube/channels/${channelId}/default-description`, {
      defaultDescription,
      defaultTags,
    }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['youtube', 'default-description', vars.channelId] })
      qc.invalidateQueries({ queryKey: ['youtube', 'channels'] })
    },
  })
}

// ─── Analytics ───

export type AnalyticsPeriod = '48h' | '7d' | '28d' | '90d' | '365d'

export function useYouTubeAnalytics(channelId: string | null, period: AnalyticsPeriod) {
  return useQuery<YouTubeAnalyticsData>({
    queryKey: ['youtube', 'analytics', channelId, period],
    queryFn: () => api.get(`/youtube/channels/${channelId}/analytics?period=${period}`),
    enabled: !!channelId,
  })
}

// ─── Tag Presets ───

export type YouTubeTagPresets = Record<string, string[]>

export function useYouTubeTagPresets() {
  return useQuery<YouTubeTagPresets>({
    queryKey: ['youtube', 'tag-presets'],
    queryFn: () => api.get('/youtube/tag-presets'),
    staleTime: 10 * 60 * 1000,
  })
}

export function useSaveYouTubeTagPresets() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (presets: YouTubeTagPresets) =>
      api.put<{ ok: boolean }>('/youtube/tag-presets', { presets }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['youtube', 'tag-presets'] }),
  })
}

// ─── Schedule Videos ───

export function useScheduleYouTubeVideos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ channelId, ...data }: {
      channelId: string
      videos: Array<{ url: string; title?: string; description?: string; tags?: string }>
      title: string
      description: string
      tags: string
      privacyStatus: string
      startDate: string
      postTime: string
    }) => api.post<{ ok: boolean; batchId: string; count: number }>(
      `/youtube/channels/${channelId}/schedule`,
      data,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['youtube'] })
    },
  })
}
